import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react"
import type { AppShell } from "@/App"
import { producaoApi, errorMessage, type BoardData, type Job } from "./api"

/**
 * Dados da Produção partilhados pelos ecrãs (quadro, semana, detalhe) e o
 * painel de atribuição (G3), que abre por cima de qualquer um e não tem rota.
 */
export interface AssignRequest {
  job: Job
  preset?: { date?: string; van_id?: string }
}

interface ProducaoCtx {
  shell: AppShell | null
  board: BoardData | null
  loading: boolean
  error: string | null
  reload(): Promise<void>
  /**
   * Modo `host` (decisão 10): ao abrir o quadro ou a semana, o estado vem do
   * contrato — relê se o que há tem mais de alguns segundos.
   */
  refreshContracts(): void
  /** Substitui uma obra no estado local (depois de gravar). */
  upsertJob(job: Job): void
  assign: AssignRequest | null
  openAssign(req: AssignRequest): void
  closeAssign(): void
  syncPending(): Promise<void>
  syncing: boolean
}

const Ctx = createContext<ProducaoCtx | null>(null)

export function ProducaoProvider({ shell, children }: { shell: AppShell | null; children: ReactNode }) {
  const [board, setBoard] = useState<BoardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [assign, setAssign] = useState<AssignRequest | null>(null)
  const [syncing, setSyncing] = useState(false)
  const autoSynced = useRef(false)
  const loadedAt = useRef(0)

  const reload = useCallback(async () => {
    if (!shell) return
    try {
      setBoard(await producaoApi.board(shell))
      loadedAt.current = Date.now()
      setError(null)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [shell])

  const syncPending = useCallback(async () => {
    if (!shell) return
    setSyncing(true)
    try {
      // Lotes pequenos no servidor; repete enquanto houver progresso.
      for (let i = 0; i < 5; i++) {
        const r = await producaoApi.syncPending(shell)
        if (r.synced === 0 || r.remaining === 0) break
      }
    } catch (err) {
      console.warn("[producao] sincronizar pendentes falhou", err)
    } finally {
      setSyncing(false)
      await reload()
    }
  }, [shell, reload])

  useEffect(() => {
    void reload()
  }, [reload])

  // As Pages Functions não têm worker: as pendentes voltam a tentar-se ao
  // abrir o quadro (uma vez por abertura da área).
  useEffect(() => {
    if (!board || autoSynced.current) return
    autoSynced.current = true
    if (board.pending_sync > 0 && board.config.trello_configured) void syncPending()
    // Modo `host`: as obras «Por adjudicar» com o pagamento já no Odoo adjudicam-se (D4).
    if (board.config.contracts_backend === "host" && shell) {
      producaoApi
        .awardsPending(shell)
        .then((r) => (r.awarded > 0 ? reload() : undefined))
        .catch((err) => console.warn("[producao] adjudicações pendentes falharam", err))
    }
  }, [board, syncPending, shell, reload])

  const refreshContracts = useCallback(() => {
    if (board?.config.contracts_backend !== "host") return
    if (Date.now() - loadedAt.current > 10_000) void reload()
  }, [board?.config.contracts_backend, reload])

  const upsertJob = useCallback((job: Job) => {
    setBoard((b) => {
      if (!b) return b
      const exists = b.jobs.some((j) => j.id === job.id)
      // O resumo do contrato (modo `host`) só vem no quadro: mantém-se até ao próximo reload.
      const jobs = exists ? b.jobs.map((j) => (j.id === job.id ? { ...job, contract: job.contract ?? j.contract } : j)) : [job, ...b.jobs]
      return {
        ...b,
        jobs,
        pending_sync: jobs.filter((j) => j.trello_sync_status === "pendente" || j.trello_sync_status === "erro").length,
      }
    })
  }, [])

  return (
    <Ctx.Provider
      value={{
        shell,
        board,
        loading,
        error,
        reload,
        refreshContracts,
        upsertJob,
        assign,
        openAssign: setAssign,
        closeAssign: () => setAssign(null),
        syncPending,
        syncing,
      }}
    >
      {children}
    </Ctx.Provider>
  )
}

export function useProducao(): ProducaoCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error("useProducao fora do ProducaoProvider")
  return c
}
